import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Printer, Mail, X, Receipt, ExternalLink } from 'lucide-react';
import { formatDateTime, formatDate } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Payment receipt viewer + Print / Email actions.
 * Fetches the receipt data on mount (which also assigns a receipt_number
 * server-side on first fetch, so subsequent opens are idempotent).
 *
 * Props:
 *   submissionId – payment_submissions._id to render as a receipt
 *   onClose      – called when the user dismisses the modal
 *   canEmail     – (default true) show/hide the Email button
 */
export const ReceiptModal = ({ submissionId, onClose, canEmail = true }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/payments/${submissionId}/receipt`,
          { withCredentials: true }
        );
        if (!cancelled) setReceipt(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load receipt');
        if (!cancelled) onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId, onClose]);

  // Uses the current cookie session — opens the PDF in a new tab so the user
  // gets the browser's native print controls.
  const printReceipt = () => {
    if (!receipt) return;
    const url = `${API}/admin/payments/${submissionId}/receipt/pdf`;
    window.open(url, '_blank', 'noopener');
  };

  const emailReceipt = async () => {
    if (!receipt) return;
    if (!receipt.resident_email) {
      toast.error(`${receipt.resident_name || 'This resident'} has no email on file — update the profile first.`);
      return;
    }
    setEmailing(true);
    try {
      await axios.post(
        `${API}/admin/payments/${submissionId}/receipt/email`,
        {},
        { withCredentials: true }
      );
      toast.success(`Receipt emailed to ${receipt.resident_email}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email receipt');
    } finally {
      setEmailing(false);
    }
  };

  const fmt = (v) => `LKR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-label="Payment receipt"
      data-testid="receipt-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#e2e8f0] flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#2563eb] text-white flex items-center justify-center">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Payment Receipt</h3>
              <p className="text-xs text-[#64748b]">
                {loading ? 'Loading…' : receipt?.receipt_number}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#94a3b8] hover:text-[#0f172a] p-1"
            aria-label="Close receipt"
            data-testid="close-receipt-modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">Preparing receipt…</p>
            </div>
          ) : receipt ? (
            <>
              <div className="text-sm space-y-1">
                <p className="text-[#64748b]">Received from</p>
                <p className="font-medium text-[#0f172a]">{receipt.resident_name}</p>
                <p className="text-xs text-[#64748b]">
                  {receipt.unit_number} · {receipt.property_name}
                </p>
              </div>

              <dl className="grid grid-cols-2 gap-y-1 text-sm">
                <dt className="text-[#64748b]">Payment date</dt>
                <dd className="text-right text-[#0f172a]">{formatDateTime(receipt.payment_date)}</dd>
                <dt className="text-[#64748b]">Method</dt>
                <dd className="text-right text-[#0f172a]">{receipt.method_label}</dd>
                {receipt.reference && (<>
                  <dt className="text-[#64748b]">Reference</dt>
                  <dd className="text-right text-[#0f172a]">{receipt.reference}</dd>
                </>)}
                <dt className="text-[#64748b]">Received by</dt>
                <dd className="text-right text-[#0f172a]">{receipt.received_by}</dd>
              </dl>

              <div className="bg-blue-50 rounded-md p-4 text-center" data-testid="receipt-amount">
                <p className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">
                  Amount received
                </p>
                <p className="text-3xl font-bold text-blue-800 mt-1" style={{ fontFamily: 'Outfit' }}>
                  {fmt(receipt.amount)}
                </p>
              </div>

              {(receipt.applied_invoices || []).length > 0 && (
                <div className="border border-[#e2e8f0] rounded-md overflow-hidden" data-testid="receipt-applied-table">
                  <div className="px-3 py-2 bg-[#f8fafc] text-[10px] uppercase tracking-widest text-[#64748b] font-semibold">
                    Bills settled ({receipt.applied_invoices.length})
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold">
                      <tr className="border-b border-[#e2e8f0]">
                        <th className="text-left px-3 py-2">Reference</th>
                        <th className="text-right px-3 py-2">Applied</th>
                        <th className="text-right px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.applied_invoices.map((a) => (
                        <tr key={a.invoice_id} className="border-b border-[#f1f5f9] last:border-b-0" data-testid={`receipt-applied-${a.invoice_id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-[#0f172a]">{a.invoice_number || '—'}</div>
                            <div className="text-[11px] text-[#64748b]">
                              {a.label || a.invoice_type}
                              {a.period_month ? ` · ${a.period_month}/${a.period_year}` : ''}
                            </div>
                            {a.description && (
                              <div className="text-[11px] italic text-[#94a3b8]">{a.description}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-[#0f172a]">{fmt(a.amount_applied)}</td>
                          <td className="px-3 py-2 text-right">
                            {a.fully_paid ? (
                              <span className="text-emerald-700 font-semibold text-xs">Paid</span>
                            ) : (
                              <span className="text-red-700 font-semibold text-xs">Bal {fmt(a.balance_after)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {receipt.overpayment > 0.005 && (
                <div className="bg-amber-50 border border-amber-100 rounded-md px-3 py-2 text-sm text-amber-800" data-testid="receipt-overpayment">
                  <b>Overpayment banked as credit:</b> {fmt(receipt.overpayment)}
                </div>
              )}

              <div className="border-t border-[#e2e8f0] pt-3 flex items-center justify-between text-sm" data-testid="receipt-arrears">
                <span className="text-[#334155]">Total arrears remaining (all invoices)</span>
                <span
                  className={`font-bold text-lg ${receipt.arrears_after_payment > 0.01 ? 'text-red-700' : 'text-emerald-700'}`}
                >
                  {fmt(receipt.arrears_after_payment)}
                </span>
              </div>
              {receipt.arrears_after_payment <= 0.01 && (
                <p className="text-xs text-emerald-700 text-center italic">
                  Account is fully paid up. Thank you!
                </p>
              )}
            </>
          ) : null}
        </div>

        <div className="p-5 border-t border-[#e2e8f0] flex flex-col sm:flex-row gap-2 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={emailing}
            data-testid="close-receipt-button"
          >
            Close
          </Button>
          {canEmail && receipt?.resident_email && (
            <Button
              onClick={emailReceipt}
              disabled={emailing || loading}
              variant="outline"
              className="text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/5"
              data-testid="email-receipt-button"
            >
              <Mail className="mr-2 h-4 w-4" />
              {emailing ? 'Sending…' : 'Email to resident'}
            </Button>
          )}
          <Button
            onClick={printReceipt}
            disabled={loading}
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
            data-testid="print-receipt-button"
          >
            <Printer className="mr-2 h-4 w-4" />
            Print / Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
